import Navigator from "../../models/navigator";
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import DataTable from "datatables.net-dt";
import "datatables.net-columncontrol-dt";
import "datatables.net-select-dt";
import type { Api } from "datatables.net";
import "datatables.net-scroller";
import tableService from "../../services/tableService";
import ProjectInfo from "../../models/projectInfo";
import TableColumnDataType from "../../models/tableColumnDataType";
import Settings from "../../models/settings";
import TableManager from "../../models/tableManager";
import Navigable, { NavigatorMove, SetFilters } from "../../models/navigable";
import NavigatorPosition from "../../models/navigatorPosition";
import promiseHelper from "../../util/promiseHelper";
import LoadingIndicatorLargeCenter from "../common/LoadingIndicatorLargeCenter";
import httpService from "../../services/httpService";
import localStorageService from "../../services/localStorageService";
import Filter from "../../models/filter";
import { TableRef } from "./Table";
import tableCommon from "./tableCommon";

interface Props {
  navigator: Navigator;
  project: ProjectInfo;
  settings: Settings;
  // children: React.ReactNode;
}

const TableVirtualized = forwardRef((props: Props, ref: Ref<TableRef>) => {
  const { navigator, project, settings } = props;

  const gridRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({ refresh }));

  const [tableManager] = useState<TableManager>(new TableManager());

  const [showHelperColumns, setShowHelperColumns] = useState<boolean>(
    settings.showTableHelperColumns,
  );

  const [filters, setFilters] = useState<Filter[]>([]);

  const [initSelectedIndex, setInitSelectedIndex] = useState<number>(-1);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const refresh = () => {
    setTimeout(() => {
      setSize();
    }, 100);
  };

  const setSize = useCallback(() => {
    const height = gridRef.current?.offsetHeight ?? 0;
    //const width = gridRef.current?.offsetWidth ?? 0;

    tableCommon.updateScrollY(height, gridRef);
  }, []);

  useEffect(() => {
    const tableNode = tableRef.current;

    if (!tableNode) {
      return;
    }

    const navigable = new Navigable();
    navigable.id = "table";

    let table: Api<any> | undefined;
    let isDisposed = false;

    let selectedIndex: number = initSelectedIndex;

    const init = async () => {
      if (!tableNode) {
        return;
      }

      setIsBusy(true);

      // row count

      const { data: tableRowsCount } = await tableService.getTableRowsCount(
        project.idProject,
        filters,
      );

      console.log("table-rowCount", tableRowsCount);

      tableManager.init(tableRowsCount);

      if (isDisposed) {
        return;
      }

      // columns

      const { data: tableColumns } = await tableService.getTableColumns(
        project.idProject,
      );

      console.log("table-columnCount", tableColumns.length);

      if (isDisposed) {
        return;
      }

      if (showHelperColumns) {
        const helperColumnNames = tableManager.getHelperColumnNames();

        for (const helperColumnName of helperColumnNames) {
          tableColumns.push({
            columnName: helperColumnName,
            displayName: helperColumnName,
            dataType: TableColumnDataType.Number,
            unitMeasure: "",
            decimals: 3,
            index: -1,
          });
        }
      }

      const columns = tableCommon.getConfigColumns(tableColumns, project);

      // table data

      table = new DataTable(tableNode, {
        deferRender: true,
        paging: true,
        scroller: true,
        scrollCollapse: true,
        scrollX: true,
        scrollY: "200px",
        searching: true,
        dom: "t",
        columns: columns,
        order: [],
        ordering: {
          indicators: false,
          handler: false,
        },
        select: { style: "single", toggleable: false } as any,

        columnControl: [],

        // virtualization

        serverSide: true,
        processing: true,
        ajax: {
          url: `${httpService.getUrl("/table/getDataTableData")}`,
          type: "POST",
          contentType: "application/json",
          processData: false,
          beforeSend: function (xhr: any, settings: any) {
            if (isDisposed) {
              return;
            }

            xhr.setRequestHeader(
              "Authorization",
              "Bearer " + localStorageService.getAccessToken(),
            );
            settings.data = JSON.stringify(settings.data);
          },
          data: function (d: any) {
            // add custom params to request
            d.idProject = project.idProject;
            d.filters = filters;
            return d;
          },
          dataSrc: function (json: any) {
            // on response

            if (isDisposed) {
              return;
            }

            const start = json.start;
            const length = json.length;

            const startIndex = start;
            const stopIndex = startIndex + length - 1;

            tableManager.addItems(json.data, startIndex, stopIndex);

            return json.data; // must return an array
          },
        } as any,
      });

      // fires one time on initialization when all is ready
      table.on("init.dt", function (e, settings, json) {
        if (isDisposed) {
          return;
        }

        // console.log("init.dt event fired");
        scrollToAndSelectIndex(table, selectedIndex);
      });

      table.on("draw.dt", function () {
        if (isDisposed) {
          return;
        }

        // console.log("onDraw");
        selectIndex(table, selectedIndex);
      });

      table.on(
        "user-select",
        async function (e, dt, type, cell, originalEvent) {
          if (isDisposed) {
            return;
          }

          if (type === "row") {
            let index = cell.index().row;

            // console.log("user-select-localIndex", index);

            index = localIndexToGlobalIndex(table, index);

            selectedIndex = index;

            // console.log("user clicked row:", table.row(cell.index().row).data());
            // console.log("user-select-globalIndex", index);

            const rowData = tableManager.getItems(index);
            if (rowData && Object.keys(rowData).length > 0) {
              await tableCommon.onRowSelected(
                rowData,
                tableManager,
                navigator,
                navigable,
                project,
              );
            }
          }
        },
      );

      setSize();

      setIsBusy(false);
    };

    const scrollToAndSelectIndex = (table: any, index: number) => {
      scrollTo(table, index);
      selectIndex(table, index);
    };

    const scrollTo = (table: any, index: number) => {
      const scrollToIndex = Math.max(0, index - 3);

      // console.log("scrollToIndex", scrollToIndex);

      table.scroller.toPosition(scrollToIndex, false);
    };

    const selectIndex = (table: any, index: number) => {
      // console.log("selectIndex", index);

      if (index === -1) {
        table.rows().deselect();
        return;
      }

      const pageStart = table.settings()[0]._iDisplayStart;
      const pageLength = table.rows({ page: "current" }).count();
      const localIndex = index - pageStart;

      // console.log(
      //   `pageStart: ${pageStart}, pageLength: ${pageLength}, localIndex: ${localIndex}`
      // );

      if (localIndex >= 0 && localIndex < pageLength) {
        // console.log("selectIndex-localIndex", localIndex);

        table.row(localIndex).select();
      }
    };

    const localIndexToGlobalIndex = (table: any, localIndex: number) => {
      const pageStart = table.settings()[0]._iDisplayStart;
      const index = localIndex + pageStart;

      // console.log(
      //   "localIndexToGlobalIndex",
      //   localIndex,
      //   pageStart,
      //   index,
      //   table.settings()[0],
      // );

      return index;
    };

    settings.onShowTableHelperColumnsChanged = (checked: boolean) => {
      setShowHelperColumns(settings.showTableHelperColumns);
      setInitSelectedIndex(selectedIndex);
    };

    const navigatorMove: NavigatorMove = async (
      navigatorPositon: NavigatorPosition,
    ) => {
      if (isDisposed) {
        return;
      }

      while (!tableManager.hasItems() || !table) {
        await promiseHelper.delay(100);
      }

      let itemIndex = tableManager.getItemIndex(
        navigatorPositon.idRoad,
        navigatorPositon.idSession,
        navigatorPositon.distance,
      );

      if (itemIndex === -1) {
        // get index from server
        const { data: rowIndex } = await tableService.getTableRowIndex(
          project.idProject,
          navigatorPositon.idRoad,
          navigatorPositon.idSession,
          navigatorPositon.distance,
          filters,
        );
        itemIndex = rowIndex;

        // console.log("getIndexFromServer", itemIndex);
      }

      if (itemIndex !== -1) {
        // console.log("table: index found", itemIndex);
        selectedIndex = itemIndex;
        scrollToAndSelectIndex(table, itemIndex);
      } else {
        // console.log("table: index not found");
        selectedIndex = -1;
        selectIndex(table, -1);
      }
    };
    navigable.navigatorMove = navigatorMove;

    const _setFilters: SetFilters = async (
      filters: Filter[],
      isProjectChanging: boolean,
    ) => {
      setFilters(filters);
    };
    navigable.setFilters = _setFilters;

    navigator.attach(navigable);

    init();

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      // console.log(`tableVirtualized:navigator:move()-show-hide-init`);
      navigatorMove(navigator.navigatorPosition);
    }

    return () => {
      navigator.detach(navigable);
      settings.onShowTableHelperColumnsChanged = undefined;
      tableManager.clearItems();
      isDisposed = true;
      // console.log("table-destory");
      table?.destroy();
      // clear table content
      tableNode.innerHTML = "";
    };
  }, [
    project,
    navigator,
    tableManager,
    setSize,
    settings,
    showHelperColumns,
    initSelectedIndex,
    filters,
  ]);

  return (
    <div
      ref={gridRef}
      style={{ height: "100%", width: "100%", fontSize: "12px", border: "0px" }}
    >
      {isBusy && <LoadingIndicatorLargeCenter />}
      <table className="display compact" ref={tableRef}></table>
    </div>
  );
});

export default TableVirtualized;
